import {
  AlertTriangle, ArrowRightLeft, BellRing, FlaskConical, Mail, ShieldAlert, Users, ClipboardCheck, Globe, ClipboardList, FileText, History, Inbox, LayoutDashboard, ListChecks, PlayCircle, SearchCheck, Settings, Store, Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { Role } from '@/auth/roles';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Hide from users below this role (UI only). */
  role?: Role;
  /** Hidden until the BFF reports the feature as set up (portal: GET /api/portal/status). */
  feature?: 'portal';
  /** A count badge the shell resolves (approvals: items still pending). Hidden at zero. */
  badge?: 'approvals';
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

/** The sidebar. Routes for these paths live in App.tsx. */
export const NAV: NavSection[] = [
  {
    id: 'billing',
    label: 'Billing',
    items: [
      { path: '/billing', label: 'Overview', icon: LayoutDashboard },
      { path: '/billing/merchants', label: 'Merchants', icon: Store },
      { path: '/billing/runs', label: 'Runs', icon: PlayCircle },
      { path: '/billing/exceptions', label: 'Exceptions', icon: AlertTriangle },
      { path: '/billing/approvals', label: 'Approvals', icon: ClipboardCheck, badge: 'approvals' },
      { path: '/billing/leakage', label: 'Leakage', icon: SearchCheck },
    ],
  },
  {
    id: 'collections',
    label: 'Collections',
    items: [
      { path: '/collections', label: 'Overview', icon: Wallet },
      { path: '/collections/worklist', label: 'Worklist', icon: ListChecks },
      { path: '/collections/invoices', label: 'Invoices', icon: FileText },
      { path: '/collections/outbox', label: 'Outbox', icon: Inbox, role: 'operator' },
      { path: '/collections/settings', label: 'Settings', icon: Settings, role: 'admin' },
    ],
  },
  {
    id: 'portal',
    label: 'Customer portal',
    items: [{ path: '/portal', label: 'Portal controls', icon: Globe, feature: 'portal' }],
  },
  {
    id: 'migration',
    label: 'Stripe migration',
    items: [
      { path: '/migration', label: 'Overview', icon: ArrowRightLeft, role: 'admin' },
      { path: '/migration/customers', label: 'Customers', icon: Users, role: 'admin' },
      { path: '/migration/exceptions', label: 'Exceptions queue', icon: ShieldAlert, role: 'admin' },
      { path: '/migration/sends', label: 'Invoice sends', icon: Mail, role: 'admin' },
      { path: '/migration/notices', label: 'Customer notices', icon: BellRing, role: 'admin' },
      { path: '/migration/pilot', label: 'Pilot', icon: FlaskConical, role: 'admin' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit',
    items: [{ path: '/audit', label: 'Audit log', icon: History }],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((s) => s.items);

/** Longest-prefix match, so /billing/merchants/CUTTERS highlights Merchants. */
export function activeNavItem(pathname: string): NavItem | undefined {
  return ALL_NAV_ITEMS.filter((i) => pathname === i.path || pathname.startsWith(`${i.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];
}

export const PAGE_ICON_FALLBACK: LucideIcon = ClipboardList;
